<template>
    <div id="kirmizi-santral-extensions">
        <div class="page-header">
            <div class="left">
                <h3>Dahililer</h3>
                <p>Kırmızı Santral telefon dahililerini yönetin</p>
            </div>
            <div class="right">
                <a-button type="primary" @click="showAddModal = true" :icon="'plus'"> Dahili Ekle </a-button>
                <a-button @click="refreshExtensions" :icon="'reload'" :loading="loading"> Yenile </a-button>
            </div>
        </div>

        <div class="content">
            <SupsisTable
                :columns="getCols()"
                :rows="extensions"
                :loading="loading"
                bottom-pagination
                :pagination-cursor="pagination"
                :options="{
                    fixedHeader: true,
                    borderX: true,
                    borderY: true,
                }"
                @cursor-changed="(cursor) => (pagination = cursor)"
            />
        </div>

        <!-- Add/Edit Extension Modal -->
        <a-modal
            :visible="showAddModal || showEditModal"
            :title="showAddModal ? 'Dahili Ekle' : 'Dahili Düzenle'"
            @ok="handleSave"
            @cancel="handleCancel"
            :confirmLoading="saving"
        >
            <a-form layout="vertical">
                <a-form-item label="Dahili Numarası" required>
                    <a-input v-model="currentExtension.extension" placeholder="Örn: 1001" :disabled="showEditModal" />
                </a-form-item>

                <a-form-item label="Görünen Ad" required>
                    <a-input v-model="currentExtension.displayname" placeholder="Dahili görünen adını girin" />
                </a-form-item>

                <a-form-item label="Şifre" required>
                    <a-input-password v-model="currentExtension.password" placeholder="Dahili şifresi" />
                </a-form-item>

                <a-form-item label="Kodekler">
                    <a-input v-model="currentExtension.codecs" placeholder="Örn: g729,alaw,ulaw" />
                </a-form-item>

                <a-form-item label="Kayıt">
                    <a-select v-model="currentExtension.recordings" style="width: 100%">
                        <a-select-option value="yes">Evet</a-select-option>
                        <a-select-option value="no">Hayır</a-select-option>
                    </a-select>
                </a-form-item>

                <a-form-item label="WebRTC">
                    <a-select v-model="currentExtension.webrtc" style="width: 100%">
                        <a-select-option value="yes">Evet</a-select-option>
                        <a-select-option value="no">Hayır</a-select-option>
                    </a-select>
                </a-form-item>

                <a-form-item label="Sesli Mesaj">
                    <a-select v-model="currentExtension.voicemail" style="width: 100%">
                        <a-select-option value="yes">Evet</a-select-option>
                        <a-select-option value="no">Hayır</a-select-option>
                    </a-select>
                </a-form-item>
            </a-form>
        </a-modal>

        <!-- Delete Confirmation Modal -->
        <a-modal
            v-model="showDeleteModal"
            title="Dahili Sil"
            @ok="confirmDelete"
            @cancel="showDeleteModal = false"
            :confirmLoading="deleting"
        >
            <p>Bu dahiliyi silmek istediğinize emin misiniz?</p>
            <p>
                <strong
                    >{{ $_.get(deletingExtension, "extension") }} -
                    {{ $_.get(deletingExtension, "displayname") }}</strong
                >
            </p>
        </a-modal>
    </div>
</template>

<script>
import Vue from "vue";
import { mapActions } from "vuex";
import SupsisTable from "../../../../../components/SupsisTable.vue";
import SupsisButton from "../../../Settings/components/SupsisButton.vue";
import { WEBPHONE_RS_API } from "../../../../../api/webphone.redsantral";

export default Vue.extend({
    name: "KirmiziSantralExtensions",
    components: {
        SupsisTable,
        SupsisButton,
    },
    props: {
        value: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            loading: false,
            saving: false,
            deleting: false,
            showAddModal: false,
            showEditModal: false,
            showDeleteModal: false,
            extensions: [],
            pagination: {
                page: 1,
                perPage: 20,
                total: 0,
            },
            currentExtension: {
                extension: "",
                displayname: "",
                password: "",
                codecs: "g729,alaw,ulaw",
                recordings: "yes",
                webrtc: "yes",
                voicemail: "no",
            },
            deletingExtension: null,
        };
    },
    methods: {
        ...mapActions({
            updateChannel: "channel/updateChannel",
        }),
        getCols() {
            return [
                {
                    key: "extension",
                    field: "extension",
                    title: "Dahili No",
                    width: 100,
                    align: "left",
                },
                {
                    key: "displayname",
                    field: "displayname",
                    title: "Görünen Ad",
                    width: 150,
                    align: "left",
                },
                {
                    key: "username",
                    field: "username",
                    title: "Kullanıcı Adı",
                    width: 150,
                    align: "left",
                },
                {
                    key: "codecs",
                    field: "codecs",
                    title: "Kodekler",
                    width: 120,
                    align: "left",
                },
                {
                    key: "recordings",
                    field: "recordings",
                    title: "Kayıt",
                    width: 80,
                    align: "center",
                    renderBodyCell: ({ row }) => {
                        return (
                            <a-tag color={row.recordings === "yes" ? "green" : "red"}>
                                {row.recordings === "yes" ? "Evet" : "Hayır"}
                            </a-tag>
                        );
                    },
                },
                {
                    key: "webrtc",
                    field: "webrtc",
                    title: "WebRTC",
                    width: 80,
                    align: "center",
                    renderBodyCell: ({ row }) => {
                        return (
                            <a-tag color={row.webrtc === "yes" ? "green" : "red"}>
                                {row.webrtc === "yes" ? "Evet" : "Hayır"}
                            </a-tag>
                        );
                    },
                },
                {
                    key: "voicemail",
                    field: "voicemail",
                    title: "Sesli Mesaj",
                    width: 100,
                    align: "center",
                    renderBodyCell: ({ row }) => {
                        return (
                            <a-tag color={row.voicemail === "yes" ? "green" : "red"}>
                                {row.voicemail === "yes" ? "Evet" : "Hayır"}
                            </a-tag>
                        );
                    },
                },
                {
                    key: "status",
                    field: "registeraddr",
                    title: "Kayıt Durumu",
                    width: 120,
                    align: "center",
                    renderBodyCell: ({ row }) => {
                        const isRegistered = !!row.registeraddr;
                        return (
                            <a-tag color={isRegistered ? "green" : "orange"}>
                                {isRegistered ? "Kayıtlı" : "Kayıtsız"}
                            </a-tag>
                        );
                    },
                },
                {
                    key: "device",
                    field: "device",
                    title: "Cihaz",
                    width: 120,
                    align: "left",
                    renderBodyCell: ({ row }) => {
                        return <span>{row.device || "-"}</span>;
                    },
                },
                {
                    key: "expiration",
                    field: "expiration",
                    title: "Son Erişim",
                    width: 140,
                    align: "left",
                    renderBodyCell: ({ row }) => {
                        if (!row.expiration) return <span>-</span>;
                        return <span>{this.$moment(row.expiration).format("DD.MM.YYYY HH:mm")}</span>;
                    },
                },
                {
                    title: "İşlemler",
                    key: "actions",
                    width: 120,
                    align: "center",
                    renderBodyCell: ({ row }) => {
                        return (
                            <div class="actions">
                                <SupsisButton
                                    onClick={() => this.editExtension(row)}
                                    small
                                    content="Düzenle"
                                    v-tippy={{ followCursor: true, placement: "bottom-end" }}
                                >
                                    <i class="material-icons text-primary">edit</i>
                                </SupsisButton>
                                <SupsisButton
                                    onClick={() => this.deleteExtension(row)}
                                    small
                                    content="Sil"
                                    v-tippy={{ followCursor: true, placement: "bottom-end" }}
                                >
                                    <i class="material-icons text-danger">delete</i>
                                </SupsisButton>
                            </div>
                        );
                    },
                },
            ];
        },
        async loadExtensions() {
            try {
                this.loading = true;
                const response = await WEBPHONE_RS_API.extensions.list();

                if (response && response.extensions) {
                    this.extensions = response.extensions.map((ext, index) => ({
                        ...ext,
                        _id: ext.extension || index.toString(),
                    }));
                    this.pagination.total = this.extensions.length;
                } else {
                    this.extensions = [];
                    this.pagination.total = 0;
                }
            } catch (error) {
                console.error("Dahili listesi yüklenirken hata:", error);
                this.$message.error("Dahili listesi yüklenirken hata oluştu");
                this.extensions = [];
                this.pagination.total = 0;
            } finally {
                this.loading = false;
            }
        },
        refreshExtensions() {
            this.loadExtensions();
        },
        editExtension(extension) {
            this.currentExtension = { ...extension };
            this.showEditModal = true;
        },
        deleteExtension(extension) {
            this.deletingExtension = extension;
            this.showDeleteModal = true;
        },
        async handleSave() {
            if (!this.currentExtension.extension || !this.currentExtension.displayname) {
                this.$message.error("Dahili numarası ve görünen ad zorunludur");
                return;
            }

            try {
                this.saving = true;

                if (this.showAddModal) {
                    // Dahili ekle
                    await WEBPHONE_RS_API.extensions.create(this.currentExtension);
                    this.$message.success("Dahili başarıyla eklendi");
                } else {
                    // Dahili güncelle
                    await WEBPHONE_RS_API.extensions.update(this.currentExtension.extension, this.currentExtension);
                    this.$message.success("Dahili başarıyla güncellendi");
                }

                this.handleCancel();
                await this.loadExtensions();
            } catch (error) {
                console.error("Dahili kaydedilirken hata:", error);
                this.$message.error("Dahili kaydedilirken hata oluştu");
            } finally {
                this.saving = false;
            }
        },
        async confirmDelete() {
            try {
                this.deleting = true;
                await WEBPHONE_RS_API.extensions.delete(this.deletingExtension.extension);

                this.showDeleteModal = false;
                this.deletingExtension = null;
                this.$message.success("Dahili başarıyla silindi");
                await this.loadExtensions();
            } catch (error) {
                console.error("Dahili silinirken hata:", error);
                this.$message.error("Dahili silinirken hata oluştu");
            } finally {
                this.deleting = false;
            }
        },
        handleCancel() {
            this.showAddModal = false;
            this.showEditModal = false;
            this.currentExtension = {
                extension: "",
                displayname: "",
                password: "",
                codecs: "g729,alaw,ulaw",
                recordings: "yes",
                webrtc: "yes",
                voicemail: "no",
            };
        },
    },
    mounted() {
        this.loadExtensions();
    },
});
</script>

<style scoped lang="scss">
#kirmizi-santral-extensions {
    padding: 20px;
    background: var(--color-bg-default);
    height: 100%;

    .page-header {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        margin-bottom: 20px;
        padding-bottom: 15px;
        border-bottom: 1px solid var(--color-border-default);

        .left {
            h3 {
                margin: 0 0 5px 0;
                color: var(--color-text-primary);
                font-size: 20px;
                font-weight: 600;
            }

            p {
                margin: 0;
                color: var(--color-text-secondary);
                font-size: 14px;
            }
        }

        .right {
            display: flex;
            gap: 10px;
        }
    }

    .content {
        background: var(--color-bg-container);
        border-radius: 6px;
        border: 1px solid var(--color-border-default);
        overflow: hidden;
    }

    .actions {
        display: flex;
        gap: 5px;
        justify-content: center;

        .supsis-button {
            background: none;
            border: none;
            padding: 3px;

            .material-icons {
                font-size: 18px;
            }
        }
    }
}
</style>
